import { useState, useEffect, useCallback, ChangeEvent, useRef } from "react";
import styled from "styled-components";
import {
  Paper,
  Grid,
  Box,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Checkbox,
  Toolbar,
} from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";

import {
  Tooltip,
  SearchInput,
  ButtonAdd,
  Loading,
  DialogYesNo,
} from "components/shared";
import { Icons } from "components/Icons";
import { RowNoData } from "components";
import { useTable, useModulockerTemplate, useRouter } from "hooks";
import { IModulockerTemplate } from "config/interfaces";
import { TableTitle } from "./styles";
import { SlotSizes } from "config/interfaces/ModulockerTemplate";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: {
      paddingBottom: theme.spacing(2),
      borderRadius: "4px",
      background: "var(--backgroundContent)",
      width: "100%",
      "& .MuiToolbar-regular": {
        padding: 0,
      },
    },
    table: {
      borderRadius: "4px",
      background: "var(--backgroundContent)",
      width: "100%",
    },
    margin: {
      margin: theme.spacing(1),
    },
    withoutLabel: {
      marginTop: theme.spacing(3),
    },
    textField: {
      maxWidth: "calc(100% - 12px)",
    },
    input: {
      "& .MuiInputBase-input": {
        boxShadow: "none",
        fontSize: "1.2rem",
        fontWeight: "500",
        textTransform: "uppercase",
      },
    },
    checkbox: {
      "&.MuiCheckbox-colorSecondary.Mui-checked": {
        color: "var(--orange)",
      },
      "& .MuiSvgIcon-root": {
        width: "1.4rem",
      },
    },
  })
);

interface TableProps {
  title?: string;
}

interface HeadCell {
  disableSorting?: boolean;
  id: keyof IModulockerTemplate.Table;
  label: string;
}

// TABLE HEAD
const headCells: HeadCell[] = [
  { id: "description", label: "MODELO" },
  { id: "sku", label: "SKU" },
  { id: "slotsP", label: "P" },
  { id: "slotsM", label: "M" },
  { id: "slotsG", label: "G" },
  { id: "slotsGG", label: "GG" },
  { id: "boxQty", label: "TOTAL" },
];

const SelectedItems = styled.span`
  text-transform: uppercase;
  font-weight: 700;
  font-size: 1.2rem;
  width: fit-content;
`;

export default function TableAddressees({ title }: TableProps) {
  const [rows, setRows] = useState(
    [] as IModulockerTemplate.ModulockerTemplate[]
  );
  const [openDeleteTemplateDialog, setOpenDeleteTemplateDialog] =
    useState(false);
  const [openCreateTemplateDialog, setOpenCreateTemplateDialog] =
    useState(false);

  const [search, setSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState([] as Array<string>);

  const router = useRouter();

  const [filterFn, setFilterFn] = useState({
    fn: (items: any) => {
      return items;
    },
  });

  const {
    loading,
    templates: records,
    deleteTemplateModulocker,
  } = useModulockerTemplate();

  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
    useTable({
      records,
      headCells,
      filterFn,
      config: { checkbox: true },
    });

  let inputSearchRef = useRef<HTMLInputElement | null>(null);

  const classes = useStyles();

  useEffect(() => {
    setRows(records);
  }, [records]);

  const onSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>, newId: string) => {
      if (event.target.checked) {
        let newSelectedRows = [...selectedRows];
        newSelectedRows.push(newId);
        setSelectedRows(newSelectedRows);
      } else {
        const newSelectedRows = selectedRows.filter((id) => id !== newId);
        setSelectedRows(newSelectedRows);
      }
    },
    [selectedRows]
  );

  const onSelectAll = useCallback((selectedRows: Array<string>) => {
    setSelectedRows(selectedRows);
  }, []);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    let target = e.target;
    setSearch(target.value);
    setFilterFn({
      fn: (items: any) => {
        if (target.value === "") return items;
        else
          return items.filter(
            (templateSearch: IModulockerTemplate.ModulockerTemplate) =>
              templateSearch.description
                .toLowerCase()
                .includes(target.value.toLowerCase())
          );
      },
    });
  };

  const handleSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    rowId: string
  ) => {
    onSelect(event, rowId);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map(
        (selectedModulockerTemplate) => selectedModulockerTemplate.uuid
      );
      return onSelectAll(newSelecteds);
    }
    onSelectAll([] as Array<string>);
  };

  const isSelected = (transactionId: string) =>
    selectedRows.indexOf(transactionId) !== -1;

  const selectedItems =
    selectedRows.length > 0 ? String(selectedRows.length).padStart(2, "0") : 0;

  const deleteMessageText = (
    <>
      <p>
        Confirma a exclusão{" "}
        {selectedRows.length > 1 ? <> dos Modelos? </> : <> do Modelo? </>}
      </p>
    </>
  );

  const messageCreateText = (
    <>
      <p>Deseja criar o template com o boxe CONTROLE padrão?</p>
    </>
  );

  const numberOfSlotsWithGiveSize = (
    modulockerIndex: number,
    size: SlotSizes
  ) => {
    return records[modulockerIndex].boxes.filter((slot) => slot.size === size)
      .length;
  };

  return (
    <>
      <Paper className={classes.toolbar} elevation={0}>
        <Toolbar>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={6} md={5}>
              <SearchInput
                ref={inputSearchRef}
                value={search}
                onChange={handleSearch}
                placeholder="POR DESCRIÇÃO"
              />
            </Grid>
          </Grid>
        </Toolbar>
      </Paper>

      {!loading ? (
        <Paper className={classes.table} elevation={0}>
          {title && <TableTitle>{title}</TableTitle>}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <span>
              <SelectedItems>SELECIONADOS: {selectedItems}</SelectedItems>
              {selectedItems ? (
                <ul style={{ display: "inline", marginLeft: "15px" }}>
                  <Tooltip title="EXCLUIR">
                    <IconButton
                      onClick={() => setOpenDeleteTemplateDialog(true)}
                    >
                      {" "}
                      <Icons.Delete style={{ fontSize: "18px" }} />{" "}
                    </IconButton>
                  </Tooltip>

                  {selectedRows.length === 1 && (
                    <Tooltip title="EDITAR">
                      <IconButton
                        onClick={() =>
                          router.push(
                            `/configuracoes/modelo/${selectedRows[0]}`
                          )
                        }
                      >
                        <Icons.Edit style={{ fontSize: "18px" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </ul>
              ) : null}
            </span>
            <ButtonAdd onClick={() => setOpenCreateTemplateDialog(true)}>
              NOVO TEMPLATE
            </ButtonAdd>
          </Box>

          <TblContainer>
            <TblHead
              numSelected={selectedRows.length}
              rowCount={rows.length}
              onSelectAllClick={handleSelectAllClick}
            />
            <TableBody>
              {recordsAfterPagingAndSorting().length === 0 ? (
                <RowNoData />
              ) : (
                recordsAfterPagingAndSorting().map(
                  (
                    row: IModulockerTemplate.ModulockerTemplate,
                    index: number
                  ) => {
                    const isItemSelected = isSelected(row.uuid);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        key={row.uuid}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            className={classes.checkbox}
                            checked={isItemSelected}
                            inputProps={{ "aria-labelledby": labelId }}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => handleSelect(event, row.uuid)}
                          />
                        </TableCell>
                        <TableCell align="left">{row.description}</TableCell>
                        <TableCell align="left">{row.sku}</TableCell>
                        <TableCell align="left">
                          {numberOfSlotsWithGiveSize(index, "P")}
                        </TableCell>
                        <TableCell align="left">
                          {numberOfSlotsWithGiveSize(index, "M")}
                        </TableCell>
                        <TableCell align="left">
                          {numberOfSlotsWithGiveSize(index, "G")}
                        </TableCell>
                        <TableCell align="left">
                          {numberOfSlotsWithGiveSize(index, "GG")}
                        </TableCell>
                        <TableCell align="left">{row.boxQty}</TableCell>
                      </TableRow>
                    );
                  }
                )
              )}
            </TableBody>
          </TblContainer>

          <TblPagination />
        </Paper>
      ) : (
        <Box mt={15} mb={15}>
          <Loading />
        </Box>
      )}

      <DialogYesNo
        title="Excluir"
        text={deleteMessageText}
        onClose={() => setOpenDeleteTemplateDialog(false)}
        onClick={() => {
          deleteTemplateModulocker(selectedRows);
          setSelectedRows([]);
          setOpenDeleteTemplateDialog(false);
        }}
        open={openDeleteTemplateDialog}
      />

      <DialogYesNo
        title="Criar novo template modulocker"
        text={messageCreateText}
        onClickYes="configuracoes/modelo/controle"
        onClickNo="configuracoes/modelo"
        onClose={() => setOpenCreateTemplateDialog(false)}
        onClick={() => {
          setOpenCreateTemplateDialog(false);
        }}
        open={openCreateTemplateDialog}
      />
    </>
  );
}
